import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { InvoiceReconciliationItem } from '../Models/InvoiceReconciliationItem';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-finance-invoice-report',
  templateUrl: './finance-invoice-report.component.html',
  styleUrls: ['./finance-invoice-report.component.css'],
  standalone: false
})
export class FinanceInvoiceReportComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['invoiceCreatedDate', 'invoiceNumber', 'subscriptionName', 'subscriptionTier', 'frequency', 'serviceType', 'notificationCount', 'creditsCount'];

  allRecords: InvoiceReconciliationItem[] = [];
  dataSource = new MatTableDataSource<InvoiceReconciliationItem>([]);

  pageSize = 50;

  dateFrom: Date;
  dateTo: Date;
  subscriptionNameSearch: string = '';
  invoiceNumberSearch: string = '';

  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {
    const now = new Date();
    this.dateTo = now;
    this.dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  }

  ngOnInit() {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loadData();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (item, column) => {
      switch (column) {
        case 'invoiceCreatedDate': return item.invoiceCreatedDate ?? '';
        case 'invoiceNumber': return item.invoiceNumber ?? '';
        case 'subscriptionName': return item.subscriptionName ?? '';
        case 'subscriptionTier': return item.subscriptionTier ?? '';
        case 'frequency': return item.frequency ?? '';
        case 'serviceType': return item.serviceType ?? '';
        case 'notificationCount': return item.notificationCount;
        case 'creditsCount': return item.creditsCount;
        default: return '';
      }
    };
  }

  async loadData() {
    try {
      let params = new HttpParams()
        .set('user', this.loggedInUserTag)
        .set('dateFrom', this.toIsoDate(this.dateFrom))
        .set('dateTo', this.toIsoDate(this.dateTo));

      this.allRecords = await lastValueFrom(
        this.http.get<InvoiceReconciliationItem[]>('/charitycentral/GetInvoiceReconciliation', { params })
      );
      this.applyFilters();
    } catch {
      this.toastr.error('Failed to load invoice reconciliation data.', '', { timeOut: 4000 });
    }
  }

  applyFilters() {
    const nameTerm = this.subscriptionNameSearch.trim().toLowerCase();
    const numTerm = this.invoiceNumberSearch.trim().toLowerCase();

    const filtered = this.allRecords.filter(r => {
      const matchesName = !nameTerm || (r.subscriptionName ?? '').toLowerCase().includes(nameTerm);
      const matchesNum = !numTerm || (r.invoiceNumber ?? '').toLowerCase().includes(numTerm);
      return matchesName && matchesNum;
    });

    this.dataSource.data = filtered;
    if (this.paginator) this.paginator.firstPage();
  }

  clearFilters() {
    this.subscriptionNameSearch = '';
    this.invoiceNumberSearch = '';
    this.applyFilters();
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
  }

  formatDate(value: string | null): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-GB');
  }

  downloadCsv() {
    const rows = this.dataSource.data;
    if (!rows.length) {
      this.toastr.info('No data to download.', '', { timeOut: 3000 });
      return;
    }

    const header = 'Invoice Date,Invoice Number,Subscription Name,Subscription Tier,Frequency,Service Type,Notification Count,Credits';
    const lines = rows.map(r =>
      `"${this.formatDate(r.invoiceCreatedDate)}","${(r.invoiceNumber ?? '').replace(/"/g, '""')}","${(r.subscriptionName ?? '').replace(/"/g, '""')}","${(r.subscriptionTier ?? '').replace(/"/g, '""')}","${(r.frequency ?? '')}","${(r.serviceType ?? '')}",${r.notificationCount},${r.creditsCount}`
    );
    const csv = [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_reconciliation_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private toIsoDate(d: any): string {
    // d may be a native Date (from constructor) or a Moment (from the datepicker).
    // Moment exposes .format(); native Date does not. Both cases must use local
    // time — toISOString() converts to UTC and shifts the date in BST/CEST etc.
    if (typeof d.format === 'function') {
      return d.format('YYYY-MM-DD');
    }
    const year = (d as Date).getFullYear();
    const month = ((d as Date).getMonth() + 1).toString().padStart(2, '0');
    const day = (d as Date).getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
